import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { formatAmount } from '~/utils/trialPricing';

/**
 * Offre annuelle « 12 mois pour le prix de 10 » (prix Stripe `price_1UNZu0EfZmrphW5EEo7yazvz`,
 * proposé seul dans le portail client, changement facturé tout de suite au prorata).
 * Montants TTC en centimes, par devise d'abonnement.
 */
export const ANNUAL_PRICE_CENTS: Record<string, number> = { eur: 49000, usd: 59000, gbp: 45000 };
export const MONTHLY_LIST_CENTS: Record<string, number> = { eur: 4900, usd: 5900, gbp: 4500 };

export type AnnualOffer = { annual: string; twelveMonths: string; trialing: boolean };

/**
 * L'encart « Passez à l'annuel » s'affiche seulement s'il est vraiment avantageux et
 * actionnable : abonnement mensuel en essai ou actif, ni résiliation programmée ni pause,
 * devise connue, et prochaine facture au prix catalogue (un client avec remise partenaire
 * paierait l'annuel plus cher que 12 mois remisés → on ne le lui propose pas).
 */
export function annualOfferFor(sub: SubscriptionStatus | null, lang: string): AnnualOffer | null {
    if (!sub || sub.interval !== 'month') return null;
    if (typeof sub.intervalCount === 'number' && sub.intervalCount !== 1) return null;
    // Calendrier d'abonnement en place : le portail Stripe refuse tout changement d'offre.
    if (sub.pauseReason === 'has_schedule') return null;
    if (sub.status !== 'active' && sub.status !== 'trialing') return null;
    if (sub.cancelAtPeriodEnd || sub.cancelAt) return null;
    if (sub.pause && sub.pause.status === 'paused') return null;
    const currency = (sub.currency || '').toLowerCase();
    const annual = ANNUAL_PRICE_CENTS[currency];
    const monthly = MONTHLY_LIST_CENTS[currency];
    if (!annual || !monthly) return null;
    if (sub.nextAmount !== monthly) return null;
    return {
        annual: formatAmount(annual, currency, lang),
        twelveMonths: formatAmount(monthly * 12, currency, lang),
        trialing: sub.status === 'trialing',
    };
}

/**
 * Lien de l'email « 2 mois offerts si vous passez à l'année » (`/account/annual?client=<id>`) :
 * que montrer sur la page Mon compte du lieu.
 * - `loading` : statut d'abonnement pas encore lu ;
 * - `offer` : l'encart annuel s'affiche (mis en avant) ;
 * - `already_yearly` : déjà en formule annuelle (lien rouvert après le changement) ;
 * - `admin_only` : membre non admin du lieu (seul l'admin change de formule) ;
 * - `unavailable` : plus proposable (remise, arrêt programmé, pause, impayé, pas d'abonnement…) ;
 * - `error` : statut illisible (serveur injoignable) — ne pas dire « plus disponible ».
 */
export type AnnualLinkState = 'loading' | 'offer' | 'already_yearly' | 'admin_only' | 'unavailable' | 'error';

export function annualLinkState(args: {
    hasBilling: boolean;
    loaded: boolean;
    sub: SubscriptionStatus | null;
    lang: string;
}): AnnualLinkState {
    if (!args.hasBilling) return 'unavailable';
    if (!args.loaded) return 'loading';
    const { sub } = args;
    if (!sub) return 'error';
    if (sub.interval === 'year' && (sub.status === 'active' || sub.status === 'trialing')) return 'already_yearly';
    if (!annualOfferFor(sub, args.lang)) return 'unavailable';
    return sub.viewerRole === 'member' ? 'admin_only' : 'offer';
}
